/**
 * DOM utility functions for efficient operations
 */
/**
 * Batch DOM reads and writes for better performance
 */
export class DOMBatcher {
    constructor() {
        this.readQueue = [];
        this.writeQueue = [];
        this.scheduled = false;
    }
    /**
     * Schedule a DOM read operation
     */
    read(fn) {
        this.readQueue.push(fn);
        this.scheduleFlush();
    }
    /**
     * Schedule a DOM write operation
     */
    write(fn) {
        this.writeQueue.push(fn);
        this.scheduleFlush();
    }
    /**
     * Schedule a flush of the queues
     */
    scheduleFlush() {
        if (!this.scheduled) {
            this.scheduled = true;
            requestAnimationFrame(() => this.flush());
        }
    }
    /**
     * Flush all queued operations
     */
    flush() {
        // Execute all reads first
        const reads = this.readQueue.splice(0);
        reads.forEach(fn => fn());
        // Then execute all writes
        const writes = this.writeQueue.splice(0);
        writes.forEach(fn => fn());
        this.scheduled = false;
    }
}
/**
 * Check if an element is in the viewport
 */
export function isInViewport(element, margin = 0) {
    const rect = element.getBoundingClientRect();
    return (rect.bottom >= -margin &&
        rect.right >= -margin &&
        rect.top <= (window.innerHeight || document.documentElement.clientHeight) + margin &&
        rect.left <= (window.innerWidth || document.documentElement.clientWidth) + margin);
}
/**
 * Get all images in the document
 */
export function getAllImages() {
    // Get images from img tags
    const imgElements = Array.from(document.getElementsByTagName('img'));
    // Get images from picture elements
    const pictureElements = Array.from(document.getElementsByTagName('picture'));
    const pictureImages = pictureElements
        .map(picture => picture.querySelector('img'))
        .filter((img) => img !== null);
    // Combine and deduplicate
    const allImages = [...new Set([...imgElements, ...pictureImages])];
    return allImages;
}
/**
 * Debounce a function
 */
export function debounce(func, wait) {
    let timeout = null;
    return function (...args) {
        const context = this;
        if (timeout !== null) {
            clearTimeout(timeout);
        }
        timeout = setTimeout(() => {
            func.apply(context, args);
            timeout = null;
        }, wait);
    };
}
/**
 * Throttle a function
 */
export function throttle(func, limit) {
    let inThrottle = false;
    return function (...args) {
        const context = this;
        if (!inThrottle) {
            func.apply(context, args);
            inThrottle = true;
            setTimeout(() => {
                inThrottle = false;
            }, limit);
        }
    };
}
/**
 * Wait for an element to appear in the DOM
 */
export function waitForElement(selector, timeout = 5000) {
    return new Promise((resolve, reject) => {
        const element = document.querySelector(selector);
        if (element) {
            resolve(element);
            return;
        }
        const observer = new MutationObserver((_mutations, obs) => {
            const element = document.querySelector(selector);
            if (element) {
                obs.disconnect();
                resolve(element);
            }
        });
        observer.observe(document.body, {
            childList: true,
            subtree: true
        });
        setTimeout(() => {
            observer.disconnect();
            reject(new Error(`Element ${selector} not found within ${timeout}ms`));
        }, timeout);
    });
}
/**
 * Get the largest image in the viewport
 */
export function getLargestViewportImage() {
    const images = getAllImages();
    const viewportImages = images.filter(img => isInViewport(img));
    if (viewportImages.length === 0)
        return null;
    return viewportImages.reduce((largest, current) => {
        const largestSize = largest.width * largest.height;
        const currentSize = current.width * current.height;
        return currentSize > largestSize ? current : largest;
    });
}
/**
 * Create a DocumentFragment from HTML string
 */
export function createFragment(html) {
    const template = document.createElement('template');
    template.innerHTML = html;
    return template.content;
}
//# sourceMappingURL=dom-utils.js.map